import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Stack, Table } from "react-bootstrap";
import {
  RECOMMENDATIONS,
  type RecommendationValue,
  type RequisitionDetail,
  type RequisitionStatus,
} from "@ld-scorecard/shared";
import { useApi } from "../api";
import { useMe } from "../App";
import StatCard from "../components/StatCard";
import { RankingBar, RecommendationDonut, REC_COLORS } from "../components/charts";
import { useFlag, FLAGS } from "../flags";

const STATUS_TONE: Record<RequisitionStatus, string> = { open: "success", filled: "info", closed: "secondary" };

function topRecommendation(tally: Record<RecommendationValue, number>): RecommendationValue | null {
  let best: RecommendationValue | null = null;
  let max = 0;
  for (const r of RECOMMENDATIONS) {
    if (tally[r.value] > max) {
      max = tally[r.value];
      best = r.value;
    }
  }
  return best;
}

const recLabel = (v: RecommendationValue) => RECOMMENDATIONS.find((r) => r.value === v)?.label ?? v;

export default function RequisitionPage() {
  const { reqId = "" } = useParams();
  const api = useApi();
  const me = useMe();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<RequisitionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const showKpis = useFlag(FLAGS.DASHBOARD_KPI_TILES);
  const analytics = useFlag(FLAGS.RESULTS_ANALYTICS);
  const rankingFlag = useFlag(FLAGS.CHART_CANDIDATE_RANKING);
  const recDonutFlag = useFlag(FLAGS.CHART_RECOMMENDATION_DONUT);
  const canArchive = useFlag(FLAGS.REQUISITION_ARCHIVE);
  const canCascadeDelete = useFlag(FLAGS.ADMIN_CASCADE_DELETE);
  const showRanking = analytics && rankingFlag;
  const showRecDonut = analytics && recDonutFlag;

  const load = () =>
    api
      .getRequisition(reqId)
      .then(setDetail)
      .catch((e) => setError(String(e.message ?? e)));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, reqId]);

  const changeStatus = async (status: RequisitionStatus) => {
    try {
      await api.updateRequisition(reqId, { status });
      await load();
    } catch (e) {
      setError(String((e as Error).message));
    }
  };

  const toggleArchive = async () => {
    const archived = !detail?.requisition.archived;
    try {
      await api.updateRequisition(reqId, { archived });
      if (archived) navigate("/");
      else await load();
    } catch (e) {
      setError(String((e as Error).message));
    }
  };

  const deleteReq = async () => {
    const n = detail?.candidates.length ?? 0;
    const msg =
      n > 0
        ? `Delete this requisition AND its ${n} candidate(s) and all their scorecards? This can't be undone.`
        : "Delete this requisition? This can't be undone.";
    if (!confirm(msg)) return;
    try {
      await api.deleteRequisition(reqId);
      navigate("/");
    } catch (e) {
      setError(String((e as Error).message));
    }
  };

  const removeCandidate = async (candidateId: string, name: string) => {
    if (!confirm(`Delete ${name} and all their scorecards? This can't be undone.`)) return;
    try {
      await api.deleteCandidate(candidateId);
      await load();
    } catch (e) {
      setError(String((e as Error).message));
    }
  };

  if (error) return <p className="text-danger">{error}</p>;
  if (!detail) return <Spinner animation="border" />;

  const { requisition: r, candidates, totalPanelists } = detail;

  const scored = candidates.filter((c) => c.aggregate && c.aggregate.weightedOverall !== null);
  const ranking = [...scored]
    .sort((a, b) => (b.aggregate!.weightedOverall ?? 0) - (a.aggregate!.weightedOverall ?? 0))
    .map((c) => {
      const rec = topRecommendation(c.aggregate!.recommendationTally);
      return {
        label: c.candidate.name,
        value: c.aggregate!.weightedOverall ?? 0,
        color: rec ? REC_COLORS[rec] : "#6366f1",
      };
    });

  // Combined recommendation tally across the req's candidates.
  const combinedTally = RECOMMENDATIONS.reduce(
    (acc, r) => {
      acc[r.value] = scored.reduce((s, c) => s + (c.aggregate!.recommendationTally[r.value] ?? 0), 0);
      return acc;
    },
    {} as Record<RecommendationValue, number>,
  );
  const anyRecs = Object.values(combinedTally).some((n) => n > 0);

  const submittedTotal = candidates.reduce((a, c) => a + c.submittedCount, 0);
  const avgOverall =
    scored.length > 0
      ? (scored.reduce((a, c) => a + (c.aggregate!.weightedOverall ?? 0), 0) / scored.length).toFixed(1)
      : "—";

  return (
    <>
      <div className="mb-2">
        <Link to="/" className="btn btn-link px-0">
          ← Requisitions
        </Link>
      </div>
      <div className="page-header d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <Stack direction="horizontal" gap={2} className="mb-1">
            <h1 className="h3 mb-0">{r.title}</h1>
            <Badge bg={STATUS_TONE[r.status]} className="text-capitalize">{r.status}</Badge>
            {r.archived && <Badge bg="dark"><i className="bi bi-archive me-1"></i>Archived</Badge>}
          </Stack>
          <div className="text-muted small d-flex flex-wrap gap-3">
            {r.department && <span><i className="bi bi-diagram-3 me-1"></i>{r.department}</span>}
            {r.hiringManager && <span><i className="bi bi-person-badge me-1"></i>{r.hiringManager}</span>}
            {r.greenhouseUrl && (
              <a href={r.greenhouseUrl} target="_blank" rel="noreferrer">
                <i className="bi bi-box-arrow-up-right me-1"></i>Greenhouse posting
              </a>
            )}
          </div>
        </div>
        {me?.role === "admin" && (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Form.Select
              size="sm"
              style={{ width: "auto" }}
              value={r.status}
              onChange={(e) => void changeStatus(e.target.value as RequisitionStatus)}
              title="Requisition status"
            >
              <option value="open">Open</option>
              <option value="filled">Filled</option>
              <option value="closed">Closed</option>
            </Form.Select>
            <Button onClick={() => setShowAdd(true)}>
              <i className="bi bi-person-plus me-1"></i>Add candidate
            </Button>
            {canArchive && (
              <Button
                variant="outline-secondary"
                onClick={() => void toggleArchive()}
                title={r.archived ? "Restore from archive" : "Archive requisition"}
              >
                <i className={`bi ${r.archived ? "bi-arrow-counterclockwise" : "bi-archive"} me-1`}></i>
                {r.archived ? "Unarchive" : "Archive"}
              </Button>
            )}
            {canCascadeDelete && (
              <Button variant="outline-danger" onClick={() => void deleteReq()} title="Delete requisition">
                <i className="bi bi-trash"></i>
              </Button>
            )}
          </div>
        )}
      </div>

      {showKpis && (
        <Row className="g-3 mb-4">
          <Col xs={6} lg={3}><StatCard label="Candidates" value={candidates.length} icon="bi-people" tone="info" /></Col>
          <Col xs={6} lg={3}><StatCard label="Scorecards submitted" value={submittedTotal} icon="bi-check2-square" tone="warning" /></Col>
          <Col xs={6} lg={3}><StatCard label="Avg weighted overall" value={`${avgOverall}${avgOverall === "—" ? "" : " / 5"}`} icon="bi-speedometer2" tone="primary" /></Col>
          <Col xs={6} lg={3}><StatCard label="Panelists" value={totalPanelists} icon="bi-person-lines-fill" tone="secondary" /></Col>
        </Row>
      )}

      {candidates.length === 0 && (
        <p className="text-muted">
          No candidates yet. {me?.role === "admin" ? "Add one to begin." : "Ask an admin to add candidates."}
        </p>
      )}

      {scored.length > 0 && (showRanking || showRecDonut) && (
        <Row className="g-3 mb-3">
          {showRanking && (
            <Col lg={showRecDonut ? 7 : 12}>
              <Card className="shadow-sm h-100">
                <Card.Header className="fw-semibold">Candidate ranking — weighted overall</Card.Header>
                <Card.Body>
                  <RankingBar items={ranking} />
                </Card.Body>
              </Card>
            </Col>
          )}
          {showRecDonut && (
            <Col lg={showRanking ? 5 : 12}>
              <Card className="shadow-sm h-100">
                <Card.Header className="fw-semibold">Recommendations (all candidates)</Card.Header>
                <Card.Body>
                  {anyRecs ? (
                    <RecommendationDonut tally={combinedTally} />
                  ) : (
                    <p className="text-muted mb-0">No recommendations recorded yet.</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      )}

      <Card className="shadow-sm">
        <Card.Header className="fw-semibold">Candidates</Card.Header>
        <Table responsive className="mb-0 align-middle">
          <thead>
            <tr>
              <th>Candidate</th>
              <th className="text-center">Submitted</th>
              <th className="text-center">Weighted overall</th>
              <th>Leaning</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c) => {
              const agg = c.aggregate;
              const rec = agg ? topRecommendation(agg.recommendationTally) : null;
              return (
                <tr key={c.candidate.candidateId}>
                  <td className="fw-semibold">{c.candidate.name}</td>
                  <td className="text-center">
                    {c.submittedCount}
                    <span className="text-muted">/{totalPanelists}</span>
                  </td>
                  <td className="text-center">
                    {c.locked ? (
                      <span className="text-muted" title="Submit your own scorecard to unlock">
                        <i className="bi bi-lock"></i>
                      </span>
                    ) : agg?.weightedOverall !== null && agg ? (
                      <Badge bg="dark">{agg.weightedOverall} / 5</Badge>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {rec ? (
                      <Badge style={{ backgroundColor: REC_COLORS[rec] }}>{recLabel(rec)}</Badge>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-end">
                    <div className="d-inline-flex gap-2">
                      <Link to={`/candidates/${c.candidate.candidateId}`} className="btn btn-primary btn-sm">
                        Score
                      </Link>
                      <Link
                        to={`/candidates/${c.candidate.candidateId}/results`}
                        className="btn btn-outline-secondary btn-sm"
                      >
                        Results
                      </Link>
                      {me?.role === "admin" && canCascadeDelete && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          title="Delete candidate"
                          onClick={() => void removeCandidate(c.candidate.candidateId, c.candidate.name)}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>

      {me?.role === "admin" && (
        <AddCandidateModal
          reqId={reqId}
          show={showAdd}
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            void load();
          }}
        />
      )}
    </>
  );
}

function AddCandidateModal({
  reqId,
  show,
  onClose,
  onAdded,
}: {
  reqId: string;
  show: boolean;
  onClose: () => void;
  onAdded: () => void;
}) {
  const api = useApi();
  const [name, setName] = useState("");
  const [panelDate, setPanelDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.addCandidate(reqId, { name: name.trim(), panelDate: panelDate || undefined });
      setName("");
      setPanelDate("");
      onAdded();
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Add candidate</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Candidate name</Form.Label>
          <Form.Control value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Form.Group>
        <Form.Group>
          <Form.Label>Panel date (optional)</Form.Label>
          <Form.Control type="date" value={panelDate} onChange={(e) => setPanelDate(e.target.value)} />
        </Form.Group>
        {error && <p className="text-danger mt-2 mb-0">{error}</p>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={!name.trim() || saving}>
          {saving ? "Adding…" : "Add"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
